import { useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectCanvasMode,
  selectCanvasModeIsOpen,
  selectCanvasModeMessage,
  selectCanvasModeUI,
  selectCanvasModeHasUnsavedChanges,
  openCanvasMode,
  closeCanvasMode,
  updateCanvasContent,
  updateCanvasTitle,
  setCanvasAutoSaving,
  setCanvasMessageSaved,
  showUnsavedChangesModal,
  hideUnsavedChangesModal,
  setCanvasSidebarWidth,
  selectMessages,
  setMessages,
} from "../../../redux/features/agent/agentSlice";
import {
  AIResponseCanvasMessage,
  MessageType,
  AIResponse,
  AIResponseMessage,
} from "winslow-core";
import { CanvasModeService } from "../../../services/CanvasModeService";
import { marked } from "marked";

export const useCanvasMode = () => {
  const dispatch = useDispatch();

  // Selectors
  const canvasMode = useSelector(selectCanvasMode);
  const isOpen = useSelector(selectCanvasModeIsOpen);
  const message = useSelector(selectCanvasModeMessage);
  const ui = useSelector(selectCanvasModeUI);
  const hasUnsavedChanges = useSelector(selectCanvasModeHasUnsavedChanges);
  const messages = useSelector(selectMessages);

  // Open Canvas Mode by converting message to canvas message
  const openCanvas = useCallback(
    async (message: AIResponseCanvasMessage | AIResponseMessage) => {
      try {
        // Call the backend to convert the message to a canvas message
        const canvasMessage = await CanvasModeService.createCanvasMessage({
          id: message.id,
          title:
            message.type === MessageType.AI_CANVAS_MESSAGE
              ? message.message.title || "Untitled Document"
              : "Untitled Document",
          content:
            message.type === MessageType.AI_CANVAS_MESSAGE
              ? message.message.content || ""
              : (message.message as any).kwargs?.content || "",
        });

        if (canvasMessage) {
          // Update the message in Redux to be AI_CANVAS_MESSAGE type
          const updatedMessages = messages.map((msg) => {
            if (msg.id === message.id) {
              return {
                ...msg,
                type: MessageType.AI_CANVAS_MESSAGE,
                message: {
                  title: canvasMessage.message.title || "Untitled Document",
                  content: canvasMessage.message.content || "",
                },
                updatedAt: new Date(),
              } as AIResponseCanvasMessage;
            }
            return msg;
          });

          dispatch(setMessages(updatedMessages));

          // Convert content to HTML for the editor
          const htmlContent = marked(
            canvasMessage.message.content || ""
          ) as string;

          const existingLinkRecordId =
            (canvasMessage as any)?.linkRecordId ??
            (canvasMessage as any)?.sources?.[0] ??
            (message as any)?.linkRecordId ??
            (message as any)?.sources?.[0] ??
            null;

          dispatch(
            openCanvasMode({
              content: htmlContent,
              messageId: message.id as number,
              title: canvasMessage.message.title || "Untitled Document",
              linkRecordId: existingLinkRecordId,
            })
          );
        }
      } catch (error) {
        console.error("Failed to create canvas message:", error);
      }
    },
    [dispatch, messages]
  );

  // Close Canvas Mode
  const closeCanvas = useCallback(() => {
    dispatch(closeCanvasMode());
  }, [dispatch]);

  // Update document content
  const updateContent = useCallback(
    (content: string) => {
      dispatch(updateCanvasContent(content));
    },
    [dispatch]
  );

  // Update document title
  const updateTitle = useCallback(
    (title: string) => {
      dispatch(updateCanvasTitle(title));
    },
    [dispatch]
  );

  // Set auto-saving state
  const setAutoSaving = useCallback(
    (isAutoSaving: boolean) => {
      dispatch(setCanvasAutoSaving(isAutoSaving));
    },
    [dispatch]
  );

  const markAsSaved = useCallback(() => {
    dispatch(setCanvasMessageSaved());
  }, [dispatch]);

  // Show unsaved changes modal
  const showUnsavedModal = useCallback(() => {
    dispatch(showUnsavedChangesModal());
  }, [dispatch]);

  // Hide unsaved changes modal
  const hideUnsavedModal = useCallback(() => {
    dispatch(hideUnsavedChangesModal());
  }, [dispatch]);

  // Update sidebar width
  const updateSidebarWidth = useCallback(
    (width: number) => {
      dispatch(setCanvasSidebarWidth(width));
    },
    [dispatch]
  );

  // Handle closing with unsaved changes check
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      showUnsavedModal();
    } else {
      closeCanvas();
    }
  }, [hasUnsavedChanges, showUnsavedModal, closeCanvas]);

  // Auto-save functionality (debounced)
  const autoSave = useCallback(
    async (content: string, title: string) => {
      if (!message) return;

      setAutoSaving(true);

      try {
        // TODO: Implement actual auto-save API call
        // For now, just simulate the save process
        await new Promise((resolve) => setTimeout(resolve, 500));

        markAsSaved();
      } catch (error) {
        console.error("Auto-save failed:", error);
        // Don't mark as saved if auto-save fails
      } finally {
        setAutoSaving(false);
      }
    },
    [message, setAutoSaving, markAsSaved]
  );

  // Check if content is different from original
  const isContentModified = useCallback(() => {
    if (!message) return false;
    // This would need the original content to compare against
    // For now, we rely on the hasChanges flag from Redux
    return message.hasChanges;
  }, [message]);

  // Get canvas mode status
  const getStatus = useCallback(() => {
    if (!isOpen) return "closed";
    if (message?.isAutoSaving) return "saving";
    if (hasUnsavedChanges) return "unsaved";
    return "saved";
  }, [isOpen, message?.isAutoSaving, hasUnsavedChanges]);

  // Keyboard shortcuts handler
  const handleKeyboardShortcut = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return false;

      const { ctrlKey, metaKey, key, shiftKey } = event;
      const isModifierPressed = ctrlKey || metaKey;

      switch (key) {
        case "s":
          if (isModifierPressed) {
            event.preventDefault();
            // Trigger save action
            if (message) {
              autoSave(message.message.content, message.message.title);
            }
            return true;
          }
          break;

        case "Escape":
          event.preventDefault();
          handleClose();
          return true;

        case "w":
          if (isModifierPressed) {
            event.preventDefault();
            handleClose();
            return true;
          }
          break;

        default:
          break;
      }

      return false;
    },
    [isOpen, message, autoSave, handleClose]
  );

  return {
    // State
    canvasMode,
    isOpen,
    message,
    ui,
    hasUnsavedChanges,

    // Actions
    openCanvas,
    closeCanvas,
    updateContent,
    updateTitle,
    setAutoSaving,
    markAsSaved,
    showUnsavedModal,
    hideUnsavedModal,
    updateSidebarWidth,
    handleClose,
    autoSave,
    isContentModified,
    getStatus,
    handleKeyboardShortcut,
  };
};
